// Color categories for "Shop efter farve" / "Shop by colour".
//
// Farveladeland's catalogue is not pre-tagged with a clean colour taxonomy,
// so each category fans out into a Storefront-API search query that catches
// the most common spellings: tag in Danish, tag in English, and a fallback
// title match. Murano-specific results are prioritised.
//
// The slug is what appears in the URL (/farver/<slug>) — keep it ASCII.

export type ColorCategory = {
  slug: string;
  labelDa: string;
  labelEn: string;
  /** italian poetic label — kept for color landing pages, but no longer shown on tiles */
  italian: string;
  /** Tailwind background class defined in src/styles.css */
  panel: string;
  /** A solid swatch oklch — used for the round chip on the homepage strip */
  swatch: string;
  /** Hand-picked Shopify product image — the prettiest example of this colour */
  image?: string;
  /** Storefront search query — covers DA tag, EN tag and title fallback */
  query: string;
  /** Short Danish description shown on the colour landing page */
  introDa: string;
  introEn: string;
};

// Farveladeland's products are NOT tagged in Shopify (verified — every product
// returns tags: []). We use Shopify's default unscoped search, which matches
// across title + description + product_type + vendor + tags. That way a lamp
// titled "Vintage Hospodka vase" still appears under Lyserød because the
// description mentions "rosa". The trade-off is occasional fuzzy matches
// (Shopify uses related-term matching), but the broader coverage is worth it
// — most products mention the colour in their Danish description.
// Title-only scope: matching colour words in product descriptions caused
// heavy cross-contamination (e.g. green Sarreguemines plates landing under
// "Rød" because the description mentioned a red tablecloth). Restricting to
// `title:` gives cleaner, more trustworthy results.
const titleAny = (terms: string[]) => terms.map((t) => `title:${t}`).join(" OR ");

export const COLOR_CATEGORIES: ColorCategory[] = [
  {
    slug: "lyserod",
    labelDa: "Lyserød",
    labelEn: "Pink",
    italian: "Rosa antico",
    panel: "panel-rosa",
    swatch: "oklch(0.83 0.10 350)",
    image:
      "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/9EEFAD68-1AA9-46CF-B877-4F0F8024252D.jpg?v=1705862242",
    query: titleAny(["lyserød", "rosa", "pink"]),
    introDa:
      "Mundblæste lamper og glas i støvet rosa — fra blød pudder til Veneziansk rosa antico. Fin på soveværelset, badeværelset eller den lille reol i entréen.",
    introEn:
      "Hand-blown lamps and glass in dusty pink — from soft powder to Venetian rosa antico. Perfect for the bedroom, the powder room or a small entryway shelf.",
  },
  {
    slug: "gul",
    labelDa: "Gul",
    labelEn: "Yellow",
    italian: "Burro",
    panel: "panel-burro",
    swatch: "oklch(0.93 0.09 95)",
    image:
      "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/79178487-2BA2-40FC-B067-9E8940DE07FA.jpg?v=1730835252",
    query: titleAny(["gul", "yellow", "amber", "rav", "gylden"]),
    introDa:
      "Smør, honning, halm. Gult glas tænder rummet på grå dage — særligt smukt over et spisebord eller i et vindue mod nord.",
    introEn:
      "Butter, honey, straw. Yellow glass lights up grey days — beautiful above a dining table or in a north-facing window.",
  },
  {
    slug: "gron",
    labelDa: "Grøn",
    labelEn: "Green",
    italian: "Salvia",
    panel: "panel-salvia",
    swatch: "oklch(0.78 0.08 158)",
    image:
      "https://cdn.shopify.com/s/files/1/0562/8516/7668/products/image_8b801aae-00d9-458b-9a37-617f4bf94acd.jpg?v=1646162662",
    query: titleAny(["grøn", "green", "salvia", "sage", "olive"]),
    introDa:
      "Salviegrøn, æble, mosgrøn. Murano-glas med en let havbrise — beroligende på køkkenbordet eller i stuen.",
    introEn:
      "Sage, apple, moss. Murano glass with a sea-breeze quality — calming on a kitchen counter or in the living room.",
  },
  {
    slug: "blaa",
    labelDa: "Blå",
    labelEn: "Blue",
    italian: "Cielo",
    panel: "panel-cielo",
    swatch: "oklch(0.86 0.07 230)",
    image:
      "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/340F85CE-F5DC-43BB-8974-E0D44AAC8FA5.jpg?v=1728759557",
    query: titleAny(["blå", "blue", "cobalt", "aqua"]),
    introDa:
      "Himmelblå, akvamarin, dyb cobalt. Det måske mest klassiske Murano-udtryk — tidløst sammen med sortmalede vægge eller hvid kalk.",
    introEn:
      "Sky blue, aquamarine, deep cobalt. Perhaps the most classic Murano expression — timeless against black-painted walls or white lime-wash.",
  },
  {
    slug: "rod",
    labelDa: "Rød",
    labelEn: "Red",
    italian: "Rubino",
    panel: "panel-rosa",
    swatch: "oklch(0.55 0.20 25)",
    image:
      "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/B9E68DBA-CDCD-4BB5-8050-A02E66098E67.jpg?v=1739637044",
    query: titleAny(["rød", "red", "rubin", "ruby", "korall", "coral"]),
    introDa:
      "Rubinrød, koral, granat. Få ting giver så meget liv til et neutralt rum som et stykke mundblæst, rødt glas.",
    introEn:
      "Ruby, coral, garnet. Few pieces bring as much life to a neutral room as a single hand-blown red glass object.",
  },
  {
    slug: "orange",
    labelDa: "Orange",
    labelEn: "Orange",
    italian: "Pesca",
    panel: "panel-pesca",
    swatch: "oklch(0.78 0.15 55)",
    image:
      "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/D741F57A-A63B-45AA-9873-BDFDE9FA4364.heic?v=1700431410&width=800",
    query: titleAny(["orange", "fersken", "peach", "terracotta", "pesca"]),
    introDa:
      "Fersken, terracotta, brændt orange. 70'er-Murano i sin solrige form — varme nok til at bære et helt rum.",
    introEn:
      "Peach, terracotta, burnt orange. 70s Murano at its sunniest — warm enough to carry an entire room.",
  },
  {
    slug: "lilla",
    labelDa: "Lilla",
    labelEn: "Purple",
    italian: "Lavanda",
    panel: "panel-lavanda",
    swatch: "oklch(0.7 0.10 295)",
    image:
      "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/B28976B4-7328-4154-9026-3D57C76F3A0D.jpg?v=1726136855",
    query: titleAny(["lilla", "purple", "violet", "lavendel", "ametyst"]),
    introDa:
      "Lavendel, ametyst, blomme. De sjældne fund — ofte mundblæst i Venedig i 60'erne og 70'erne.",
    introEn:
      "Lavender, amethyst, plum. The rare finds — often hand-blown in Venice during the 60s and 70s.",
  },
  {
    slug: "hvid",
    labelDa: "Hvid",
    labelEn: "White",
    italian: "Latte",
    panel: "panel-nebbia",
    swatch: "oklch(0.97 0.006 90)",
    image:
      "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/D6E525C3-BAF9-4AC1-B9E3-8FAF884D388D.jpg?v=1710108862",
    query: titleAny(["hvid", "white", "opal", "latte", "mælk"]),
    introDa:
      "Opal, mælkeglas, kalk. Det stille valg — for dig der vil mærke formen før farven.",
    introEn:
      "Opal, milk glass, chalk. The quiet choice — for those who want to feel the form before the colour.",
  },
  {
    slug: "beige",
    labelDa: "Beige",
    labelEn: "Beige",
    italian: "Sabbia",
    panel: "panel-sabbia",
    swatch: "oklch(0.86 0.04 80)",
    image:
      "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/1C2794CD-1593-40BD-8FD8-7836893B417F.jpg?v=1759935177",
    query: titleAny(["beige", "creme", "cream", "sand", "taupe", "ecru", "off-white"]),
    introDa:
      "Sand, creme, taupe. Bløde, jordnære toner der lægger sig roligt ind i ethvert rum.",
    introEn:
      "Sand, cream, taupe. Soft, earthy tones that settle calmly into any room.",
  },
  {
    slug: "messing",
    labelDa: "Messing",
    labelEn: "Brass",
    italian: "Ottone",
    panel: "panel-ottone",
    swatch: "oklch(0.75 0.10 80)",
    image:
      "https://cdn.shopify.com/s/files/1/0562/8516/7668/files/66921E56-53E8-47AC-B657-D4A4EB2D4E8E.heic?v=1705261217&width=800",
    query: titleAny(["messing", "brass", "guld", "gold", "bronze", "bronce"]),
    introDa:
      "Messing, bronze, guld. Varme metaller fra 60'erne og 70'erne — patineret af tid og hænder.",
    introEn:
      "Brass, bronze, gold. Warm metals from the 60s and 70s — patinated by time and hands.",
  },
];

export function getColorBySlug(slug: string): ColorCategory | undefined {
  return COLOR_CATEGORIES.find((c) => c.slug === slug);
}
